/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect, useContext } from "react";
import { TextField } from "@mui/material";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { Logger, red } from "cms/utils/log/logger";
import { useAuth } from "cms/auth/hooks/use-auth";
import LabelledField from "components/labelled-field/index";
import { CarouselImageEditor } from "cms/components/components/hero-carousel/edit/image/carousel-image-editor";
import { isGuid, isStringEmpty } from "utils/empty-exists";
import { cloneObjectsArray } from "utils/array/index";
import { ComponentTimelineContext } from "cms/components/components/timeline/edit/component-timeline-context";

/**
 *    This is the slide editor component
 * */
const TimelineSlideEditor = () => {
    const { token } = useAuth();

    const componentTimelineContext = useContext(ComponentTimelineContext);
    const list = componentTimelineContext?.list;
    const item = componentTimelineContext?.item;
    const index = componentTimelineContext?.index;
    const updateItems = componentTimelineContext?.updateItems;

    const _milestone = item?.milestone;
    const _timeframe = item?.timeframe;
    const _image = item?.image;

    const initial_image_guid = _image?.image;
    const _settings = _image?.settings;

    const [imageLoading, setImageLoading] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);

    const [milestone, setMilestone] = useState(_milestone);
    const [timeframe, setTimeframe] = useState(_timeframe);
    const [image, setImage] = useState();
    const [settings, setSettings] = useState(_settings);

    const onChangeMilestone = (event) => {
        const milestone = event.target.value;
        setMilestone(milestone);
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].milestone = milestone;
        updateItems(nextSlides);
    };

    const onChangeTimeframe = (event) => {
        const timeframe = event.target.value;
        setTimeframe(timeframe);
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].timeframe = timeframe;
        updateItems(nextSlides);
    };

    const onChangeImageData = (imageData) => {
        // console.debug(`[TimelineSlideEditor] [onChangeImageData] image`, index, imageData)
        // console.debug(`[TimelineSlideEditor] [onChangeImageData] list`, list)
        const { image, settings } = imageData; // image is object here
        setImage(image);
        setSettings(settings);
        const { guid } = image || {};
        // console.info(`[onChangeImageData] [index:${index}] [guid:${guid}]`, image)
        if (isStringEmpty(guid)) {
            Logger.warn("[onChangeImageData] image guid missing", index, image, guid);
        } else {
            const newImageData = {
                image: guid, // image is guid here
                settings,
            };
            // console.info(`[onChangeImageData] newImageData [index:${index}] [guid:${guid}]`, newImageData)
            const nextSlides = cloneObjectsArray(list);
            nextSlides[index].image = newImageData;
            Logger.info(red(`[onChangeImageData] next slides ${index}`), nextSlides);
            updateItems(nextSlides);
            setImageFailed(false);
        }
    };
    const onRemoveImage = () => {
        Logger.debug("[onRemoveImage]");
        setImage(undefined);
        setSettings(undefined);
        const newImageData = {
            image: undefined,
            settings: undefined,
        };
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].image = newImageData;
        updateItems(nextSlides);
        setImageFailed(false);
    };

    const fetchImage = async (guid) => {
        if (isGuid(guid)) {
            setImageLoading(true);
            try {
                let currentImage = await SimpliCityAdminSDK.image.findOne(token, guid);
                setImage(currentImage);
                setImageLoading(false);
            } catch (err) {
                console.error(err);
                setImageFailed(true);
            }
        } else {
            setImageLoading(false);
            setImageFailed(false);
        }
    };

    useEffect(() => {
        fetchImage(initial_image_guid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial_image_guid]);

    return (
        <div className="simplicity-editor-frame hero-editor-container">
            <div className="component-editor-image-wrapper">
                <div className="component-editor-image-wrapper-cell">
                    {!imageLoading && (
                        <CarouselImageEditor
                            image={image}
                            image_guid={image?.guid}
                            settings={settings}
                            onChangeImageData={onChangeImageData}
                            onRemoveImage={onRemoveImage}
                        />
                    )}
                    {imageFailed && (
                        <>
                            <CarouselImageEditor
                                image={undefined}
                                image_guid={undefined}
                                settings={settings}
                                onChangeImageData={onChangeImageData}
                                onRemoveImage={onRemoveImage}
                            />
                        </>
                    )}
                </div>
                <div className="timeline-component-editor-image-details-wrapper-cell">
                    <div className="timeline-component-editor-image-details-wrapper-item">
                        <LabelledField label="Milestone" className="timeline-component-editor-image-details-label">
                            {/*  Milestone  */}
                            <TextField
                                id="milestone-title"
                                fullWidth={true}
                                value={milestone}
                                placeholder="Survey Open"
                                onChange={onChangeMilestone}
                            />
                        </LabelledField>
                    </div>
                    <div className="timeline-component-editor-image-details-wrapper-item">
                        <LabelledField label="Timeframe" className="timeline-component-editor-image-details-label">
                            {/*  Timeframe  */}
                            <TextField
                                id="timeframe-title"
                                fullWidth={true}
                                value={timeframe}
                                placeholder="Jan - Feb 2025"
                                onChange={onChangeTimeframe}
                            />
                        </LabelledField>
                    </div>
                </div>
            </div>
        </div>
    );
};
TimelineSlideEditor.displayName = "TimelineSlideEditor";
export { TimelineSlideEditor };
